/**
 * Combat Preset Functions
 * 
 * Reusable combat behaviors that can be called from any NPC behavior.
 * Just functions - no classes, no state machines.
 */

import type { AITickAPI, DetectionAPI, DamageAPI, DeathAPI } from '../hooks/NPCHookAPI';

// ============================================
// DETECTION PRESETS
// ============================================

/**
 * Aggressive detection - alert and start chasing.
 */
export const aggressiveDetect = (api: DetectionAPI): void => {
  api.actions.alert();
  api.actions.chase();
};

/**
 * Passive detection - ignore the player entirely.
 */
export const passiveDetect = (_api: DetectionAPI): void => {
  // Do nothing - player is ignored
};

/**
 * Defensive detection - alert but don't chase.
 */
export const defensiveDetect = (api: DetectionAPI): void => {
  api.actions.alert();
  // Don't chase - wait for player to come to us
};

/**
 * Flee detection - run away from player.
 */
export const fleeDetect = (api: DetectionAPI): void => {
  api.actions.flee();
};

// ============================================
// COMBAT TICK PRESETS
// ============================================

export interface CombatState {
  cooldownRemaining: number;
  attackCooldown: number;
  attackRange: number;
  isAttacking: boolean; // Track if we initiated an attack
  attackLockTimer: number; // Time remaining in attack animation (don't move/turn)
}

/** Default attack lock duration in seconds */
const ATTACK_LOCK_DURATION = 0.8;

/**
 * Aggressive combat - attack player when in range.
 * Always faces the player when attacking.
 * Returns true if currently in attack lock (caller should not move).
 */
export const aggressiveTick = (api: AITickAPI, state: CombatState): boolean => {
  // Tick down cooldown and attack lock
  state.cooldownRemaining = Math.max(0, state.cooldownRemaining - api.deltaTime);
  state.attackLockTimer = Math.max(0, state.attackLockTimer - api.deltaTime);
  
  // If in attack lock, stop movement and maintain facing
  if (state.attackLockTimer > 0) {
    api.actions.setMovement(0);
    return true; // Signal: don't do other movement
  }
  
  // Face the player when in combat range
  if (api.playerDistance <= state.attackRange * 2) {
    api.actions.setFacing(api.playerDirection);
  }
  
  // Attack if player in range and cooldown ready
  if (api.playerDistance <= state.attackRange && state.cooldownRemaining <= 0) {
    // Face player FIRST, then attack
    api.actions.setFacing(api.playerDirection);
    api.actions.setMovement(0); // Stop to attack
    
    // Trigger attack (rising edge only - combat system handles the rest)
    if (!state.isAttacking) {
      api.actions.setAttack(true);
      state.isAttacking = true;
      state.attackLockTimer = ATTACK_LOCK_DURATION;
    }
    state.cooldownRemaining = state.attackCooldown;
    return true;
  } else if (state.isAttacking) {
    // Attack ended - clear flag, need one frame with attack=false for rising edge
    state.isAttacking = false;
    api.actions.setAttack(false);
  }
  
  return false; // No attack lock, caller can move
};

/**
 * Passive combat - never attack.
 */
export const passiveTick = (api: AITickAPI): void => {
  api.actions.setAttack(false);
};

// ============================================
// DAMAGE PRESETS
// ============================================

/**
 * Default damage handling - flash and play hit sound.
 */
export const defaultDamage = (api: DamageAPI): void => {
  api.actions.flash();
  api.actions.playSound('hit');
};

/**
 * Armored damage - reduce incoming damage.
 */
export const armoredDamage = (api: DamageAPI, reduction: number): void => {
  api.amount *= (1 - reduction);
  api.actions.flash();
  api.actions.playSound('armor_hit');
};

/**
 * Immune damage - cancel all damage.
 */
export const immuneDamage = (api: DamageAPI): void => {
  api.actions.cancel();
  api.actions.playSound('immune');
};

// ============================================
// DEATH PRESETS
// ============================================

/**
 * Default death - just remove the NPC.
 */
export const defaultDeath = (api: DeathAPI): void => {
  api.actions.remove();
};

/**
 * Loot death - drop loot then remove.
 */
export const lootDeath = (api: DeathAPI, lootTable: string): void => {
  api.actions.spawnLoot(lootTable);
  api.actions.remove();
};

/**
 * Respawn death - remove then respawn after delay.
 */
export const respawnDeath = (api: DeathAPI, delay: number): void => {
  api.actions.remove();
  api.actions.respawn(delay);
};

// ============================================
// FACTORY HELPERS
// ============================================

/** Create initial combat state from config */
export const createCombatState = (
  attackRange: number = 5.0,
  attackCooldown: number = 1.0
): CombatState => ({
  cooldownRemaining: 0,
  attackCooldown,
  attackRange,
  isAttacking: false,
  attackLockTimer: 0,
});
